import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import {
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncSelectionCondition,
  SelectionMode,
} from '@mode-vegas/vegas';
import { SearchInputComponent, trackByFactory } from '@mode/capra';
import { ChartDate } from '@mode/charts/util-vegas';
import { ReportFilterDomainResult } from '@mode/filters/util';
import { ReportTypes } from '@mode/shared/contract-common';
import { formatInUTC, generateUUID } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { BehaviorSubject, Subject, debounceTime, filter, takeUntil } from 'rxjs';

interface FilterValueItem {
  value: number | string | boolean | undefined | null;
  label: string;
  selected: boolean;
}

@Component({
  selector: 'mode-single-select',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchInputComponent, ScrollingModule],
  templateUrl: './single-select.component.html',
  styleUrls: ['./single-select.component.scss'],
})
export class SingleSelectComponent implements OnInit, OnDestroy, OnChanges {
  @Input({ required: true }) filter!: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
  @Input() filterOption!: ReportFilterDomainResult;
  @Input() compiledField: CompiledField | null | undefined;
  @Input() isLoading = true;
  @Input() showAll = true;
  @Input() isOpen = false;
  @Input() sortSelectedFirst = true;

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport | undefined;

  @Output() filterChange = new EventEmitter<EncDiscreteFilterField | EncDiscreteBooleanFilterField>();

  @ViewChild('selectAllInput') selectAllInput: ElementRef | undefined;

  trackByValue = trackByFactory<FilterValueItem>(({ value }) => value);

  readonly itemHeight = 24;

  searchTerm = '';
  filteredValueList: Array<FilterValueItem> = [];
  unfilteredValueList: Array<FilterValueItem> = [];
  selected!: string;

  isAll = false;
  isExclude = false;
  uniqueId = '';
  minimumContainerHeight = `${this.itemHeight * 2}px`;

  visibleFilterValues$ = new BehaviorSubject<Array<FilterValueItem>>([]);
  searchTerm$ = new BehaviorSubject('');
  isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((searchTerm) => searchTerm != null),
        debounceTime(400)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;
        const lowercasedTerm = searchTerm.toLowerCase();
        this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
          filterValue.label.toLowerCase().includes(lowercasedTerm)
        );
        this.visibleFilterValues$.next(this.filteredValueList);
      });

    this.uniqueId = generateUUID().replace(/-/g, '');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter !== undefined) {
      const currentSelection = this.filter.selection;
      this.isAll = currentSelection?.mode === SelectionMode.ALL || currentSelection?.mode === SelectionMode.EXCLUDE_ALL;
      this.isExclude =
        currentSelection?.mode === SelectionMode.EXCLUDE || currentSelection?.mode === SelectionMode.EXCLUDE_ALL;
    }

    if (changes.isOpen && this.cdkVirtualScrollViewPort) {
      this.cdkVirtualScrollViewPort.scrollToIndex(0);
      this.cdkVirtualScrollViewPort.checkViewportSize();
    }

    if (changes.filterOption && this.filterOption) {
      this.buildValueList(this.filterOption);
      this.minimumContainerHeight = this.calculateContainerHeight();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isSelected(selected: boolean) {
    if (this.isAll) {
      return false;
    } else {
      return selected;
    }
  }

  onSelectAll() {
    this.isAll = true;

    this.onSelectionChange();
    if (this.selectAllInput?.nativeElement !== undefined) {
      // The checkbox makes the selection/deselection regardless of what
      // the model says. We need to make sure that it is correct.
      this.selectAllInput.nativeElement['checked'] = this.isAll;
    }
  }

  onSelectItem(value: ReportTypes.ReportFilterValue) {
    this.isAll = false;
    this.unfilteredValueList.forEach((filterValue) => {
      if (filterValue.value !== value) {
        filterValue.selected = false;
      } else {
        filterValue.selected = true;
      }
    });
    this.onSelectionChange();
  }

  onSelectionChange() {
    const selectionCondition = this.buildSelectionCondition();
    this.filter = {
      ...this.filter,
      selection: selectionCondition,
    };

    this.filterChange.emit(this.filter);
  }

  buildSelectionCondition(): EncSelectionCondition {
    if (this.isAll) {
      return {
        selected: [],
        mode: this.isExclude ? SelectionMode.EXCLUDE_ALL : SelectionMode.ALL,
      };
    }

    const selectedList = this.unfilteredValueList
      .filter((filterValue) => filterValue.selected)
      .map((filterValue) => (filterValue.value == null ? null : filterValue.value));

    return {
      selected: selectedList,
      mode: this.isExclude ? SelectionMode.EXCLUDE : SelectionMode.INCLUDE,
    };
  }

  buildValueList(result: ReportFilterDomainResult | undefined) {
    const selectionCondition = this.filter.selection;
    if (result?.type === Attr.Type.Discrete) {
      const selection = selectionCondition?.selected?.[0];
      const selected: FilterValueItem[] = [];
      const results: FilterValueItem[] = [];

      // Sorting shouldn't be necessary at the moment because Flamingo automatically applies a data sort to the results
      result.values.forEach((v, ii) => {
        const label = result.labels?.[ii] || this.formatValue(v);
        if (this.sortSelectedFirst && v === selection) {
          selected.push({
            value: v,
            label,
            selected: true,
          });
        } else {
          results.push({
            value: v,
            label,
            selected: this.isAll || v === selection,
          });
        }
      });

      this.unfilteredValueList = selected.concat(results);
      this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
        filterValue.label.includes(this.searchTerm)
      );
      this.visibleFilterValues$.next(this.filteredValueList);
    }
  }

  // Per: https://stackoverflow.com/a/67089033
  calculateContainerHeight(): string {
    // Account for additional "Select All" option
    const totalItems = this.unfilteredValueList.length + (this.showAll ? 1 : 0);
    // The final number of items you want to keep visible (1-10, inclusive)
    const visibleItems = Math.min(Math.max(totalItems, 1), 10);
    return `${this.itemHeight * visibleItems}px`;
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  formatValue(value: string | number | boolean | undefined | null) {
    if (value === null || value === undefined) {
      return 'Null';
    }
    if (typeof value === 'string') {
      return value;
    }

    // Only used for chart filters that have a compiledField passed in
    // this is applicable to chart filters, but not report filters
    if ((this.compiledField && this.compiledField?.hasDateFunc) || this.compiledField?.isFormulaDate) {
      if (this.compiledField.hasDatePart) {
        switch (this.compiledField.dateFuncUnit) {
          case DateTime.Unit.Quarter:
            return ChartDate.makeDatePartQuarterName(value);
          case DateTime.Unit.Month:
            return ChartDate.makeDatePartMonthName(value);
          case DateTime.Unit.Weekday:
            return ChartDate.makeDatePartWeekdayName(value);
          default:
            return String(value);
        }
      }
      return formatInUTC(new Date(+value), 'yyyy-MM-dd HH:mm:ss');
    }
    return String(value);
  }

  get isShowRelevantValuesOnly() {
    return this.filter.showRelevantValuesOnly ?? false;
  }
}
